import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import WorkIcon from "@mui/icons-material/Work";
import {
  Box,
  Button,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";
import { Link } from "react-router-dom";
import { InternJobsHealthCard_metrics$key } from "./__generated__/InternJobsHealthCard_metrics.graphql";
import formatNumber from "../../../utils/formatNumber";

const metricsFragment = graphql`
  fragment InternJobsHealthCard_metrics on JobHealthMetrics {
    totalJobs
    successCount
    failureCount
    runningCount
    retryCount
    successRate
    avgDurationMs
    jobsLastHour
    failuresLastHour
  }
`;

type Props = {
  metrics: InternJobsHealthCard_metrics$key;
};

const InternJobsHealthCard = ({ metrics: metricsKey }: Props) => {
  const metrics = useFragment(metricsFragment, metricsKey);

  const isHealthy = metrics.successRate >= 90;
  const isWarning = metrics.successRate >= 70 && metrics.successRate < 90;

  const healthStatus =
    metrics.totalJobs === 0
      ? { color: "#6b7280", label: "No Jobs" }
      : isHealthy
      ? { color: "#10b981", label: "Healthy" }
      : isWarning
      ? { color: "#f59e0b", label: "Warning" }
      : { color: "#ef4444", label: "Critical" };

  const formatDuration = (ms: number | null | undefined) => {
    if (ms == null) return "—";
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        bgcolor: "var(--intern-bg-primary)",
        border: "1px solid var(--intern-border-default)",
        borderRadius: 2,
        height: "100%",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <WorkIcon sx={{ color: "var(--intern-accent)", fontSize: 20 }} />
          <Typography
            variant="subtitle2"
            sx={{
              color: "var(--intern-text-secondary)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: "0.6875rem",
            }}
          >
            Jobs Health
          </Typography>
        </Stack>
        <Button
          component={Link}
          to="/intern/jobs"
          size="small"
          variant="outlined"
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            fontSize: "0.6875rem",
            py: 0.5,
            px: 1.5,
            minWidth: 0,
          }}
        >
          View All
        </Button>
      </Stack>

      {/* Health Status Indicator */}
      <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: healthStatus.color,
            boxShadow: `0 0 8px ${healthStatus.color}`,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: healthStatus.color,
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        >
          {healthStatus.label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "var(--intern-text-tertiary)",
            fontSize: "0.75rem",
          }}
        >
          • {metrics.successRate.toFixed(1)}% success rate
        </Typography>
      </Stack>

      {/* Stats Row */}
      <Typography
        variant="caption"
        sx={{
          color: "var(--intern-text-tertiary)",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontSize: "0.625rem",
          display: "block",
          mb: 1.5,
        }}
      >
        Last 24 Hours
      </Typography>

      <Stack
        direction="row"
        spacing={0}
        sx={{
          bgcolor: "var(--intern-bg-secondary)",
          borderRadius: 1,
          overflow: "hidden",
          mb: 2.5,
        }}
      >
        {/* Success */}
        <Tooltip title="Successful jobs">
          <Box
            sx={{
              flex: 1,
              p: 1.5,
              textAlign: "center",
              borderRight: "1px solid var(--intern-border-default)",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={0.5}
              mb={0.5}
            >
              <CheckCircleIcon sx={{ fontSize: 14, color: "#10b981" }} />
              <Typography
                variant="caption"
                sx={{
                  color: "var(--intern-text-tertiary)",
                  fontSize: "0.625rem",
                }}
              >
                Success
              </Typography>
            </Stack>
            <Typography
              variant="h6"
              sx={{
                color: "#10b981",
                fontWeight: 700,
                fontSize: "1rem",
              }}
            >
              {formatNumber(metrics.successCount)}
            </Typography>
          </Box>
        </Tooltip>

        {/* Failures */}
        <Tooltip title="Failed jobs">
          <Box
            sx={{
              flex: 1,
              p: 1.5,
              textAlign: "center",
              borderRight: "1px solid var(--intern-border-default)",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={0.5}
              mb={0.5}
            >
              <ErrorIcon sx={{ fontSize: 14, color: "#ef4444" }} />
              <Typography
                variant="caption"
                sx={{
                  color: "var(--intern-text-tertiary)",
                  fontSize: "0.625rem",
                }}
              >
                Failed
              </Typography>
            </Stack>
            <Typography
              variant="h6"
              sx={{
                color:
                  metrics.failureCount > 0
                    ? "#ef4444"
                    : "var(--intern-text-primary)",
                fontWeight: 700,
                fontSize: "1rem",
              }}
            >
              {formatNumber(metrics.failureCount)}
            </Typography>
          </Box>
        </Tooltip>

        {/* Running */}
        <Tooltip title="Currently running jobs">
          <Box
            sx={{
              flex: 1,
              p: 1.5,
              textAlign: "center",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={0.5}
              mb={0.5}
            >
              <PlayArrowIcon sx={{ fontSize: 14, color: "#3b82f6" }} />
              <Typography
                variant="caption"
                sx={{
                  color: "var(--intern-text-tertiary)",
                  fontSize: "0.625rem",
                }}
              >
                Running
              </Typography>
            </Stack>
            <Typography
              variant="h6"
              sx={{
                color:
                  metrics.runningCount > 0
                    ? "#3b82f6"
                    : "var(--intern-text-primary)",
                fontWeight: 700,
                fontSize: "1rem",
              }}
            >
              {formatNumber(metrics.runningCount)}
            </Typography>
          </Box>
        </Tooltip>
      </Stack>

      {/* Progress Bar */}
      <Box sx={{ mb: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" mb={0.5}>
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-secondary)",
              fontSize: "0.6875rem",
            }}
          >
            Success Rate
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-primary)",
              fontWeight: 600,
              fontSize: "0.6875rem",
            }}
          >
            {metrics.successRate.toFixed(1)}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={metrics.successRate}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: "var(--intern-bg-tertiary)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 3,
              bgcolor: isHealthy
                ? "#10b981"
                : isWarning
                ? "#f59e0b"
                : "#ef4444",
            },
          }}
        />
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Additional Stats */}
      <Stack spacing={1.5}>
        {/* Jobs Last Hour */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <HourglassEmptyIcon
              sx={{ fontSize: 14, color: "var(--intern-text-tertiary)" }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-secondary)",
                fontSize: "0.6875rem",
              }}
            >
              Jobs (last hour)
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-primary)",
              fontWeight: 600,
              fontSize: "0.6875rem",
            }}
          >
            {metrics.jobsLastHour}
            {metrics.failuresLastHour > 0 && (
              <Typography
                component="span"
                sx={{
                  color: "#ef4444",
                  fontWeight: 600,
                  fontSize: "0.6875rem",
                  ml: 0.5,
                }}
              >
                ({metrics.failuresLastHour} failed)
              </Typography>
            )}
          </Typography>
        </Stack>

        {/* Retries */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <ReplayIcon
              sx={{ fontSize: 14, color: "var(--intern-text-tertiary)" }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-secondary)",
                fontSize: "0.6875rem",
              }}
            >
              Retries
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            sx={{
              color:
                metrics.retryCount > 0
                  ? "#f59e0b"
                  : "var(--intern-text-primary)",
              fontWeight: 600,
              fontSize: "0.6875rem",
            }}
          >
            {metrics.retryCount}
          </Typography>
        </Stack>

        {/* Avg Duration */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-secondary)",
              fontSize: "0.6875rem",
            }}
          >
            Avg Duration
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-primary)",
              fontWeight: 600,
              fontSize: "0.6875rem",
            }}
          >
            {formatDuration(metrics.avgDurationMs)}
          </Typography>
        </Stack>

        {/* Total Jobs */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-secondary)",
              fontSize: "0.6875rem",
            }}
          >
            Total Jobs (24h)
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-primary)",
              fontWeight: 600,
              fontSize: "0.6875rem",
            }}
          >
            {formatNumber(metrics.totalJobs)}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default InternJobsHealthCard;
